import { useMemo } from 'react';
import { parseISO, format } from 'date-fns';
import { useAppContext } from '../contexts/AppContext';

/**
 * Groups files by both date and hour for the weekly calendar view
 * Hour key format: "yyyy-MM-dd'T'HH" (e.g., "2026-01-17T14")
 */
export function useFilesByDateTime(filesOverride, sourceIndices) {
  const { files: contextFiles } = useAppContext();
  const files = filesOverride || contextFiles;
  const hasSourceIndexMap =
    Array.isArray(sourceIndices) && sourceIndices.length === files.length;

  const filesByDateTime = useMemo(() => {
    const groupedByHour = new Map();
    const groupedByDate = new Map();
    const unassignedFiles = [];
    const unassignedIndices = new Set();

    files.forEach((file, localIndex) => {
      const index = hasSourceIndexMap ? sourceIndices[localIndex] : localIndex;
      const dateStr = file.modifiedExifData?.dateTaken;

      if (!dateStr) {
        unassignedFiles.push(file);
        unassignedIndices.add(index);
        return;
      }

      try {
        const parsedDate = parseISO(dateStr);

        // Group by date (yyyy-MM-dd)
        const dateKey = format(parsedDate, 'yyyy-MM-dd');
        if (!groupedByDate.has(dateKey)) {
          groupedByDate.set(dateKey, []);
        }
        groupedByDate.get(dateKey).push({ file, index });

        // Group by hour (yyyy-MM-dd'T'HH)
        const hourKey = format(parsedDate, "yyyy-MM-dd'T'HH");
        if (!groupedByHour.has(hourKey)) {
          groupedByHour.set(hourKey, []);
        }
        groupedByHour.get(hourKey).push({ file, index });
      } catch (error) {
        // If date parsing fails, treat as unassigned
        unassignedFiles.push(file);
        unassignedIndices.add(index);
      }
    });

    return { groupedByHour, groupedByDate, unassigned: unassignedFiles, unassignedIndices };
  }, [files, sourceIndices, hasSourceIndexMap]);

  return filesByDateTime;
}
